import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import { useChangedFlag } from '@/hooks/util';
import { convertToHtml } from '@/utils';

interface Props {
    subject?: string;
    bodyBeforeLink?: string;
    bodyAfterLink?: string;
    signature?: string;
}

export default function EmailTemplatePreview({
    subject,
    bodyBeforeLink,
    bodyAfterLink,
    signature,
}: Props) {
    const theme = useTheme();

    const hasSubjectChanged = useChangedFlag(subject);
    const hasBodyBeforeLinkChanged = useChangedFlag(bodyBeforeLink);
    const hasBodyAfterLinkChanged = useChangedFlag(bodyAfterLink);
    const hasSignatureChanged = useChangedFlag(signature);

    return (
        <Box
            sx={{
                borderRadius: '8px',
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                backgroundColor: 'neutral.white',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                height: 'fit-content',
                width: '100%',
                margin: '32px 32px 32px 0px',
            }}
        >
            <Box>
                <Typography variant="h2">Template preview</Typography>
                <Typography variant="body2" sx={{ marginTop: '8px' }}>
                    This is a preview of an email that your clients will see in their inbox.
                </Typography>
            </Box>
            <Divider orientation="horizontal" />
            <Box
                sx={{
                    borderRadius: '4px',
                    border: '1px solid #E0E3E5',
                    background: '#F0F5FD',
                    display: 'flex',
                    flexDirection: 'column',
                    '& > *': {
                        padding: '16px',
                    },
                    wordWrap: 'break-word',
                    overflowWrap: 'anywhere',
                }}
            >
                <Typography
                    variant="h3"
                    className={hasSubjectChanged ? 'activity-tracker' : ''}
                    sx={{ maxWidth: '549px' }}
                >
                    {subject ?? ''}
                </Typography>
                <Typography variant="body1" color="text.primary">
                    Hi [client name],
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', maxWidth: '549px' }}
                    className={hasBodyBeforeLinkChanged ? 'activity-tracker' : ''}
                >
                    <span
                        dangerouslySetInnerHTML={{ __html: convertToHtml(bodyBeforeLink ?? '') }}
                    ></span>
                </Typography>
                <Box>
                    <Link
                        href="#"
                        variant="h3"
                        sx={{
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            textDecoration: 'none',
                        }}
                    >
                        Intake form link
                    </Link>
                </Box>
                <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', maxWidth: '549px' }}
                    className={hasBodyAfterLinkChanged ? 'activity-tracker' : ''}
                >
                    <span
                        dangerouslySetInnerHTML={{ __html: convertToHtml(bodyAfterLink ?? '') }}
                    ></span>
                </Typography>
                <Typography
                    variant="body1"
                    color="text.primary"
                    className={hasSignatureChanged ? 'activity-tracker' : ''}
                >
                    <span
                        dangerouslySetInnerHTML={{ __html: convertToHtml(signature ?? '') }}
                    ></span>
                </Typography>
            </Box>
        </Box>
    );
}
